/**
 * Corporate sign-in · two-step OTP flow.
 *   Step 1: enter work email → POST /api/corporate/{slug}/sign-in
 *   Step 2: enter 6-digit code → POST /api/corporate/sign-in/verify
 *
 * On verify success, backend sets an httpOnly cookie and returns
 * `{ needs_terms_accept, member, tenant }`. We redirect to
 * `/:slug/terms` if T&C acceptance is required, else `/:slug/dashboard`.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { corporateApi } from "@/lib/corporate-api";
import CorporateShell from "./CorporateShell";

const T = {
  bg: "#F4EFE6", card: "#FCFAF4", ink: "#211C15", ink2: "#5C5447",
  ink3: "#8A8071", line: "#E5DCCB", line2: "#D8CDB7", brass: "#8B6914",
  danger: "#B84B3C",
};

export default function CorporateSignIn() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(null);
  const [step,   setStep]   = useState("email"); // email | code
  const [email,  setEmail]  = useState("");
  const [firstName, setFirstName] = useState("");
  const [otp,    setOtp]    = useState("");
  const [busy,   setBusy]   = useState(false);
  const [msg,    setMsg]    = useState(null);
  const [err,    setErr]    = useState(null);
  const otpRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const t = await corporateApi.portal(slug);
        if (alive) setTenant(t);
      } catch {
        if (alive) navigate(`/${slug}`, { replace: true });
      }
    })();
    return () => { alive = false; };
  }, [slug, navigate]);

  async function submitEmail(e) {
    e.preventDefault();
    setErr(null); setMsg(null); setBusy(true);
    try {
      const res = await corporateApi.signInStart(slug, {
        email: email.trim().toLowerCase(),
        first_name: firstName.trim() || undefined,
      });
      setMsg(res.message || "Check your work email for a 6-digit code.");
      setStep("code");
      setTimeout(() => otpRef.current?.focus(), 150);
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    } finally { setBusy(false); }
  }

  async function submitOtp(e) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const res = await corporateApi.signInVerify({
        slug,
        email: email.trim().toLowerCase(),
        otp:   otp.trim(),
      });
      if (res.needs_terms_accept) {
        navigate(`/${slug}/terms`, { replace: true });
      } else {
        navigate(`/${slug}/dashboard`, { replace: true });
      }
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    } finally { setBusy(false); }
  }

  const domains = tenant?.email_domains || [];

  return (
    <CorporateShell tenant={tenant} member={null} showSignOut={false}>
      <div style={{ maxWidth: 460, margin: "48px auto 0" }}>
        <p style={{ letterSpacing: "0.14em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 12px",
                    fontWeight: 700 }}>
          {tenant ? `Sign in · ${tenant.display_name}` : "Sign in"}
        </p>
        <h1 style={{ fontFamily: "'Fraunces', Georgia, serif",
                     fontSize: "clamp(28px, 4.4vw, 40px)", fontWeight: 500,
                     lineHeight: 1.1, color: T.ink, margin: "0 0 32px" }}>
          {step === "email"
            ? "Sign in with your work email."
            : "Enter the code we sent you."}
        </h1>

        {msg && (
          <div data-testid="corp-signin-msg"
               style={{ background: T.card, border: `1px solid ${T.line}`,
                        borderRadius: 8, padding: "12px 14px", marginBottom: 20,
                        color: T.ink2, fontSize: 13 }}>
            {msg}
          </div>
        )}
        {err && (
          <div data-testid="corp-signin-err"
               style={{ background: "#FDECE9", border: `1px solid ${T.danger}55`,
                        borderRadius: 8, padding: "12px 14px", marginBottom: 20,
                        color: T.danger, fontSize: 13 }}>
            {err}
          </div>
        )}

        {step === "email" ? (
          <form data-testid="corp-signin-email-form" onSubmit={submitEmail}>
            <label style={{ display: "block", fontSize: 11,
                            letterSpacing: "0.14em", textTransform: "uppercase",
                            color: T.ink3, marginBottom: 8, fontWeight: 600 }}>
              First name <span style={{ color: T.ink3 }}>· optional</span>
            </label>
            <input
              data-testid="corp-signin-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Riya"
              autoComplete="given-name"
              style={inputStyle}
            />

            <label style={{ display: "block", fontSize: 11,
                            letterSpacing: "0.14em", textTransform: "uppercase",
                            color: T.ink3, marginTop: 20, marginBottom: 8,
                            fontWeight: 600 }}>
              Work email
            </label>
            <input
              data-testid="corp-signin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={domains[0] ? `you@${domains[0]}` : "you@company.com"}
              autoComplete="email"
              style={inputStyle}
            />
            {domains.length > 0 && (
              <p style={{ color: T.ink3, fontSize: 12, marginTop: 8 }}>
                Accepted:{" "}
                <span style={{ fontFamily: "ui-monospace, monospace" }}>
                  {domains.map((d) => `@${d}`).join(" · ")}
                </span>
              </p>
            )}

            <button
              data-testid="corp-signin-send-otp"
              type="submit"
              disabled={busy || !email}
              style={{ ...primaryBtn, marginTop: 28,
                       opacity: busy || !email ? 0.6 : 1,
                       cursor: busy || !email ? "not-allowed" : "pointer" }}
            >
              {busy ? "Sending code…" : "Send me a code →"}
            </button>
          </form>
        ) : (
          <form data-testid="corp-signin-otp-form" onSubmit={submitOtp}>
            <label style={{ display: "block", fontSize: 11,
                            letterSpacing: "0.14em", textTransform: "uppercase",
                            color: T.ink3, marginBottom: 8, fontWeight: 600 }}>
              6-digit code
            </label>
            <input
              ref={otpRef}
              data-testid="corp-signin-otp"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
              style={{ ...inputStyle,
                       fontFamily: "ui-monospace, monospace",
                       fontSize: 22, letterSpacing: "0.4em",
                       textAlign: "center" }}
              required
            />
            <p style={{ color: T.ink3, fontSize: 12, marginTop: 8 }}>
              Signed in as{" "}
              <span style={{ fontFamily: "ui-monospace, monospace",
                             color: T.ink2 }}>{email}</span>{" "}
              · Code expires in 10 min.
            </p>

            <button
              data-testid="corp-signin-verify"
              type="submit"
              disabled={busy || otp.length !== 6}
              style={{ ...primaryBtn, marginTop: 28,
                       opacity: busy || otp.length !== 6 ? 0.6 : 1,
                       cursor: busy || otp.length !== 6 ? "not-allowed" : "pointer" }}
            >
              {busy ? "Verifying…" : "Verify & sign in →"}
            </button>

            <button
              data-testid="corp-signin-change-email"
              type="button"
              onClick={() => { setStep("email"); setOtp(""); setMsg(null); setErr(null); }}
              style={{ marginTop: 14, background: "transparent",
                       color: T.ink3, border: 0, fontSize: 12,
                       cursor: "pointer", padding: "6px 0",
                       textDecoration: "underline" }}
            >
              Use a different email
            </button>
          </form>
        )}
      </div>
    </CorporateShell>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: T.card, color: T.ink,
  border: `1px solid ${T.line2}`, borderRadius: 10,
  padding: "12px 14px", fontSize: 15, fontFamily: "inherit",
  outline: "none",
};

const primaryBtn = {
  width: "100%", background: T.ink, color: T.bg, border: 0,
  borderRadius: 999, padding: "14px 20px", fontSize: 13,
  fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase",
  fontFamily: "inherit",
};
